/**
 * useGroupTypeConfig — derives per-type feature flags and meta from a group doc.
 * Falls back to safe defaults if backend hasn't returned `features` / `type_meta`
 * (e.g. on legacy cached responses), so the UI never crashes.
 *
 * Source of truth: backend `services/group_type_config.py`. Keep keys in sync.
 */

// Canonical type list — kept in sync with backend TYPE_DEFAULTS.
export const GROUP_TYPES = ['sports', 'trip', 'event', 'daily'];

const FALLBACK_FEATURES = {
  sports: {
    sessions: true, fines: true, recurring: true, fund_contribution: true,
    loans: true, currency_picker: false, trip_phase_banner: false,
    event_budget_gauge: false, monthly_view: false, match_day_mode: true,
  },
  trip: {
    sessions: false, fines: false, recurring: false, fund_contribution: true,
    loans: true, currency_picker: true, trip_phase_banner: true,
    event_budget_gauge: false, monthly_view: false, match_day_mode: false,
  },
  event: {
    sessions: false, fines: false, recurring: false, fund_contribution: true,
    loans: false, currency_picker: false, trip_phase_banner: false,
    event_budget_gauge: true, monthly_view: false, match_day_mode: false,
  },
  daily: {
    sessions: false, fines: false, recurring: true, fund_contribution: true,
    loans: true, currency_picker: false, trip_phase_banner: false,
    event_budget_gauge: false, monthly_view: true, match_day_mode: false,
  },
};

const TYPE_LABELS = {
  sports: { label: 'Sports', tagline: 'Weekly games, fees & fines' },
  trip:   { label: 'Trip',   tagline: 'One-off journey' },
  event:  { label: 'Event',  tagline: 'Single occasion' },
  daily:  { label: 'Daily / Household', tagline: 'Recurring expenses' },
};

// Map legacy types to canonical.
function canonicalType(rawType) {
  if (rawType === 'custom') return 'daily';
  return GROUP_TYPES.includes(rawType) ? rawType : 'daily';
}

export default function useGroupTypeConfig(group) {
  const rawType = group?.group_type;
  const canonical = canonicalType(rawType);

  // Prefer backend-provided values (they apply admin overrides on top of defaults).
  const features = group?.features ?? FALLBACK_FEATURES[canonical];
  const typeMeta = group?.type_meta ?? { canonical_type: canonical, ...TYPE_LABELS[canonical] };

  return {
    canonicalType: canonical,
    typeLabel: typeMeta.label,
    typeTagline: typeMeta.tagline,
    features,
    // Convenience boolean shortcuts for common branches.
    isSports: canonical === 'sports',
    isTrip: canonical === 'trip',
    isEvent: canonical === 'event',
    isDaily: canonical === 'daily',
  };
}
